import type { User } from 'firebase/auth'
import { useParams } from 'react-router'
import { MEMBER_ROLES, type MemberDoc } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { useStaffDirectory } from '@/data/client'
import { useDoc } from '@/data/hooks'

/** What the account page writes to users/{uid} when someone uploads their own photo. */
export type UserPhoto = { photoURL?: string | null; photoPath?: string | null }

/** The photo from the Google account they sign in with, if any. */
export function googlePhoto(user: User | null): string | null {
  return user?.providerData.find((p) => p.providerId === 'google.com')?.photoURL ?? null
}

/**
 * The signed-in person as the header shows them: best name, photo and what their access is called.
 * Photo order: one they uploaded, their Google photo, their team directory photo, then initials.
 */
export function useAccountIdentity() {
  const { user, staff, role } = useAuth()
  const { clientId } = useParams()
  const member = useDoc<MemberDoc>(role === 'client' && clientId && user ? `clients/${clientId}/members/${user.uid}` : null)
  const record = useDoc<UserPhoto>(user ? `users/${user.uid}` : null)
  const directory = useStaffDirectory(role === 'staff')
  const email = user?.email?.toLowerCase() ?? ''
  const profile = role === 'staff' ? directory.items.find((s) => s.email?.toLowerCase() === email) : undefined
  const name = member.data?.name || user?.displayName || profile?.name || user?.email || ''
  const roleLabel = staff ? staff.roleName : member.data ? MEMBER_ROLES[member.data.role].label : 'Client'
  const uploaded = record.data?.photoURL ?? null
  const photo = uploaded || googlePhoto(user) || user?.photoURL || profile?.photoPath || null
  return { name, email: user?.email ?? '', photo, uploaded, photoPath: record.data?.photoPath ?? null, roleLabel }
}
